import React, { useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { IoMail, IoPhonePortrait, IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { RiLockPasswordLine } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import LoginImage from "../assets/LoginImg.png";
import { API_BASE_URL } from "../config/api";

const Login = () => {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState("email"); // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    phoneNumber: "",
    password: "",
    general: ""
  });
  const [showPassword, setShowPassword] = useState(false);

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\d{10,15}$/; // Simple regex for 10-15 digits
    return phoneRegex.test(phoneNumber.replace(/\D/g, '')); // Remove non-digits before testing
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    setErrors({
      ...errors,
      [name]: "",
      general: ""
    });
    
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const toggleLoginMethod = () => {
    // Clear previous errors
    setErrors({
      email: "",
      phoneNumber: "",
      password: "",
      general: ""
    });
    
    // Toggle between email and phone
    setLoginMethod(loginMethod === "email" ? "phone" : "email");
    
    // Clear the values of both fields to prevent confusion
    setFormData({
      ...formData,
      email: "",
      phoneNumber: ""
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to save token with expiration
  const saveTokenWithExpiry = (token) => {
    const now = new Date();
    // Set expiry time to 7 days (1 week) from now
    const expiryTime = now.getTime() + (7 * 24 * 60 * 60 * 1000);
    
    const tokenData = {
      value: token,
      expiry: expiryTime
    };
    
    localStorage.setItem('authToken', JSON.stringify(tokenData));
  };

  const validateForm = () => {
    let formIsValid = true;
    const newErrors = {
      email: "",
      phoneNumber: "",
      password: "",
      general: ""
    };

    if (loginMethod === "phone") {
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
        formIsValid = false;
      } else if (!validatePhoneNumber(formData.phoneNumber)) {
        newErrors.phoneNumber = "Please enter a valid phone number (10-15 digits)";
        formIsValid = false;
      }
    } else { // email method
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
        formIsValid = false;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
        formIsValid = false;
      }
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create the proper request body format based on login method
      const requestBody = {
        password: formData.password.trim()
      };
      
      // Add only the relevant field based on login method
      if (loginMethod === "email") {
        requestBody.email = formData.email.trim();
      } else {
        requestBody.phoneNumber = formData.phoneNumber.trim();
      }
      
      console.log("Sending login request:", requestBody);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      const data = await response.json();
      console.log("Login response:", data);
      
      if (!response.ok) {
        // Handle specific error cases
        if (data.error === "INVALID_PHONE") {
          setErrors({...errors, phoneNumber: "Invalid phone number format"});
          throw new Error("Invalid phone number format");
        } else if (data.error === "USER_NOT_FOUND") {
          throw new Error("No account found with these credentials");
        } else if (data.error === "INVALID_PASSWORD") {
          throw new Error("Incorrect password");
        } else {
          throw new Error(data.message || "Login failed");
        }
      }
      
      // Save token to localStorage with expiry
      if (data.token) {
        saveTokenWithExpiry(data.token);
      }
      
      toast.success("Login successful!");
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (error) {
      console.error("Login failed", error);
      setErrors({...errors, general: error.message || "Login failed"});
      toast.error(error.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="flex flex-col md:flex-row min-h-[calc(100vh-110px)]">
      {/* Image Side */}
      <div className="hidden md:block md:w-1/2 bg-[#E6E9F9]">
        <img
          src={LoginImage}
          alt="Shopping cart with phone"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Form Side */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md bg-white p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl md:text-3xl font-bold mb-2 text-center">Log in to your account</h1>
          <p className="text-gray-600 mb-6 text-center">Enter your details below</p>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.general}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Login Method Toggle */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                className={`px-4 py-2 rounded-l-md ${loginMethod === "email" ? "bg-purple-500 text-white" : "bg-gray-200 text-gray-600"}`}
                onClick={() => loginMethod !== "email" && toggleLoginMethod()}
              >
                Email
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-r-md ${loginMethod === "phone" ? "bg-purple-500 text-white" : "bg-gray-200 text-gray-600"}`}
                onClick={() => loginMethod !== "phone" && toggleLoginMethod()}
              >
                Phone
              </button>
            </div>

            {/* Conditional Input Field Based on Login Method */}
            {loginMethod === "email" ? (
              <div className="relative">
                <div className={`flex items-center border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md overflow-hidden`}>
                  <div className="bg-gray-100 p-3">
                    <IoMail className="text-gray-500 text-xl" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-3 focus:outline-none"
                  />
                </div>
                {errors.email && <p className="mt-1 text-red-500 text-sm">{errors.email}</p>}
              </div>
            ) : (
              <div className="relative">
                <div className={`flex items-center border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md overflow-hidden`}>
                  <div className="bg-gray-100 p-3">
                    <IoPhonePortrait className="text-gray-500 text-xl" />
                  </div>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="w-full p-3 focus:outline-none"
                  />
                </div>
                {errors.phoneNumber && <p className="mt-1 text-red-500 text-sm">{errors.phoneNumber}</p>}
              </div>
            )}
            
            <div className="relative">
              <div className={`flex items-center border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-md overflow-hidden`}>
                <div className="bg-gray-100 p-3">
                  <RiLockPasswordLine className="text-gray-500 text-xl" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full p-3 focus:outline-none pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <IoEyeOffOutline className="text-xl" />
                  ) : (
                    <IoEyeOutline className="text-xl" />
                  )}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-red-500 text-sm">{errors.password}</p>}
            </div>

            <div className="flex justify-end">
              <button 
                type="button"
                onClick={handleForgotPassword}
                className="text-red-500 text-sm hover:underline"
              >
                Forget Password?
              </button>
            </div>

            <button
              type="submit"
              className="w-full bg-purple-500 text-white py-3 rounded-lg hover:bg-purple-600 transition-colors mt-4 font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging In...
                </div>
              ) : "Log In"}
            </button>

            <div className="relative flex items-center justify-center my-4">
              <div className="border-t border-gray-300 absolute w-full"></div>
              <span className="bg-white px-3 text-sm text-gray-500 relative">OR</span>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full border border-gray-300 py-3 rounded-lg flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <FcGoogle className="text-xl" />
              Log in with Google
            </button>
          </form>

          <div className="text-center mt-6">
            <span className="text-gray-600">Don't have an account?</span>{" "}
            <a href="/signup" className="text-purple-600 font-semibold hover:underline">
              Sign up
            </a>
          </div>
        </div>
      </div>
      
      {/* Toastify Container */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Login;